import { Button, Card, Col, DatePicker, Input, Row } from 'antd';
import React, { ReactElement, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { searchInPatientList, setPageIndex, setTpaGlobalSearch, updateDropdownSearchFilter } from '../../../store/actions/inPatientAction';
import { GUTTOR_SPACE_SMALL } from '../../constants/commonConstants';
import { replaceRsAndComma } from '../../../utils/utility';
import AmountRangeSelector from '../AmountRangeSelector/AmountRangeSelector';
import MultiSelectCheckbox from '../MultiSelectCheckbox/MultiSelectCheckbox';
import { get } from 'lodash';
import MultiselectDropdown from '../MultiselectDropdown/MultiselectDropdown';
import { SearchOutlined } from '@ant-design/icons';
import { searchClaimList, setPageIndexList } from '../../../store/actions/grievanceAction';
import { customGTMEvent } from '../../../modules/crm/inPatient/service/crmUtils';
import { Payer } from '../../models/payer.model';

const FilterDropdownComponent = (props: any): ReactElement => {
  const {
    filterKey, setSelectedKeys, confirm, inputType, placeholder,
    optionPath, labelKey = '', additionalLabelKey, additionalLabelKeyDivider, additionalPath,
  } = props;
  const dispatch = useDispatch();
  const selectedFilters = useSelector((state: RootState): any => state.selectedInPatientDetail.filters);
  const {tpaGlobalSearch} = useSelector((state: RootState): any => state.selectedInPatientDetail);
  const rootState = useSelector((state: RootState): RootState => state || {});
  const { RangePicker } = DatePicker;
  const inputRef = useRef<any>(null);

  useEffect((): any => {
    if ( inputRef.current) {
      setTimeout((): any => {
        inputRef?.current?.focus();
       });
    }
  });

  useEffect((): any => {
    setSelectedKeys(selectedFilters[filterKey]);
    const captureTag = setTimeout((): any => {
      const selectedKeys = selectedFilters[filterKey];
      if (filterKey && selectedKeys !== undefined) {
        triggerCustomGTMFilterEvent(filterKey, selectedKeys, rootState);
      }
      /* clear the datalayer so that redundant information is not passed
      during the next occurence of the next event */
      (window as any).dataLayer.push({
        [filterKey] : undefined,
        payerName: undefined,
        status: undefined,
      });
      }, 1000);
      return (): any => {
        clearTimeout(captureTag);
      };
    }, [selectedFilters]);

  const onSearch = (): any => {
    dispatch(setTpaGlobalSearch({ ...tpaGlobalSearch, value: '' }));
    dispatch(setPageIndex(1));
    dispatch(searchInPatientList());
    dispatch(setPageIndexList(1));
    dispatch(searchClaimList());
    confirm();
  };

  const inputChangeHandler = (event: any): void => {
    dispatch(updateDropdownSearchFilter({ [filterKey]: event.target.value }));
    if (event.target.value === '') {
      onSearch();
    }
  };

  const dateOnChange = (event: any): any => {
    dispatch(updateDropdownSearchFilter({ [filterKey]: event }));
    if (!event) {
      onSearch();
    }
  };

  const onAmountChange = (value: any, position: number, isClear: boolean = false): any => {
    if (!isClear) {
      const updatedEvent = [...(selectedFilters[filterKey] || [])];
      updatedEvent[position] = replaceRsAndComma(value);
      dispatch(updateDropdownSearchFilter({ [filterKey]: updatedEvent }));
    } else {
      dispatch(updateDropdownSearchFilter({ [filterKey]: ['', ''] }));
    }
    if (!value) {
      onSearch();
    }
  };

  const getAvailableOptionsFromState = (state: RootState): any => {
    const availableOptions = Object.assign(get(state, optionPath));
    const updatedAvailableOptions = availableOptions.map((availableOption: any): any => {
      if (additionalLabelKey && availableOption[labelKey]) {
        const additionalData =
          availableOption[additionalLabelKey]?.length > 0
          ? `${additionalLabelKeyDivider} ${availableOption[additionalLabelKey]}`
          : '';

        availableOption.value = `${availableOption[labelKey]} ${additionalData}`;
        return availableOption;
      } if (labelKey && availableOption[labelKey]) {
        return availableOption.value = availableOption[labelKey];
      } else {
        return availableOption;
      }
    });

    return updatedAvailableOptions;
  };
// for Payer dropdown options
  const getAvailablePayerOptionsFromState = (state: RootState): any => {
    const nonConfiguredPayerList = Object?.assign?.([], get?.(state, additionalPath) ?? []);
    const disabledPayers: any[] = nonConfiguredPayerList.filter((payer: any): any =>
    !payer?.isEnabled)?.map((disabledPayer: any): any => ({
      ...disabledPayer, id: disabledPayer?.payerID, name: disabledPayer?.payerName,
      value: String(disabledPayer?.payerID), key: String(disabledPayer?.payerID),
      isRpaPayer: disabledPayer?.isRPAPayer, isOnMailPayer: !disabledPayer?.isRPAPayer,
      uniqueIdentifier: disabledPayer?.payerID,
    }));
    const configuredPayerList = Object?.assign?.(get(state, optionPath));
    const updatedAvailableOptions = configuredPayerList?.concat(disabledPayers)?.map((availableOption: any): any => {
      if (additionalLabelKey && availableOption[labelKey]) {
        const additionalData =
          availableOption[additionalLabelKey]?.length > 0
          ? `${additionalLabelKeyDivider} ${availableOption[additionalLabelKey]}`
          : '';

        availableOption.value = `${availableOption[labelKey]} ${additionalData}`;
        return availableOption;
      } if (labelKey && availableOption[labelKey]) {
        return availableOption.value = availableOption[labelKey];
      } else {
        return availableOption;
      }
    });
    if (additionalPath) {
    const finalOptions = updatedAvailableOptions?.reduce((acc: Payer[], ele: any): any => {
      const existingPayerIndex = acc?.findIndex((payer: any): any => (payer?.shortName && ele?.shortName
      && payer?.shortName === ele?.shortName));
      if (existingPayerIndex > -1) {
        const existingPayer = acc[existingPayerIndex];
        const hasNoRpaOrMail = !ele?.isRpaPayer && !ele?.isOnMailPayer && !ele?.blockedActions?.NEWADMISSION;
        if (!existingPayer?.isRpaPayer && !existingPayer?.isOnMailPayer && !existingPayer?.blockedActions?.NEWADMISSION) {
            // Keep the existing one if it has neither isRpaPayer nor isOnMailPayer and does not block NEWADMISSION
        } else if (hasNoRpaOrMail) {
          acc[existingPayerIndex] = ele; // Replace with current ele if it meets the new strongest condition
        } else if (ele?.isRpaPayer && existingPayer?.isOnMailPayer) {
          acc[existingPayerIndex] = ele; // Replace with isRpaPayer if existing is isOnMailPayer
        } else if (ele?.isOnMailPayer && existingPayer?.isRpaPayer) {
          acc[existingPayerIndex] = ele; // Replace with isOnMailPayer if existing is isRpaPayer
        }
      } else {
        acc?.push(ele);
      }

      return acc;
    }, []);

    return finalOptions;
  } else {
    return updatedAvailableOptions;
  }
  };

  const onMultiSelectCheckboxChange = (value: boolean, key: any): any => {
    const updatedEvent = [...(selectedFilters[filterKey] || [])];
    if (!value) {
      updatedEvent.splice(updatedEvent.indexOf(key), 1);
    } else {
      updatedEvent.push(key);
    }
    dispatch(updateDropdownSearchFilter({ [filterKey]: updatedEvent }));
  };

  const onMultiselectDropdownChange = (value: any): any => {
    dispatch(updateDropdownSearchFilter({ [filterKey]: value }));
    if (value.length <= 0) {
      onSearch();
    }
  };

  const triggerCustomGTMFilterEvent: any = (filter: string, selectedKeys: any[], root: any): any => {
    const eventData: any = {
        event: `Search by ${filter}`,
        [filterKey]: selectedKeys,
    };

    if (filter === 'payerId') {
      const filteredPayerOptions = getAvailablePayerOptionsFromState(root)?.filter((option: any): boolean =>
        selectedKeys?.includes(option.key)) ?? [];
      eventData['payerName'] = filteredPayerOptions?.map((option: any): string => option.name) ?? [];
    } else if (filter === 'statusId') {
      const filteredStatusOptions = getAvailableOptionsFromState(root)?.filter((option: any): boolean =>
        selectedKeys?.includes(option.key)) ?? [];
      eventData['status'] = filteredStatusOptions?.map((option: any): string => option.value) ?? [];
    }
    customGTMEvent(eventData);
  };

  const getInputField = (): ReactElement | null => {
    switch (inputType) {
      case 'TEXT':
        return (
          <Row className="custom-amount-range">
            <Col>
              <Input
                placeholder={placeholder}
                allowClear={true}
                size="middle"
                ref={inputRef}
                onChange={inputChangeHandler}
                value={selectedFilters[filterKey]}
                data-testid={filterKey}
                className="input-field"
                onPressEnter={onSearch}
              />
            </Col>
            <Col md={2}>
              <Button type="primary" icon={<SearchOutlined />} className="search-btn" onClick={onSearch} />
            </Col>
          </Row>
        );
      case 'DATE_RANGE':
        return (
          <Row className="custom-amount-range ant-input-affix-wrapper-focused">
            <Col md={20} className="text-input">
              <RangePicker
                allowClear={true}
                ref={inputRef}
                onChange={dateOnChange}
                value={selectedFilters[filterKey]}
                className="no-right-border-radius-rangepicker"
              />
            </Col>
            <Col md={4}>
              <Button type="primary" icon={<SearchOutlined />} className="w-100" onClick={onSearch}/>
            </Col>
          </Row>
        );
      case 'AMOUNT':
        return (
          <AmountRangeSelector
            value={selectedFilters[filterKey]}
            onChange={onAmountChange}
            onSearch={onSearch}

          />
        );
      case 'MULTISELECT_CHECKBOX':
        return (
          <MultiSelectCheckbox
            options={getAvailableOptionsFromState(rootState)}
            value={selectedFilters[filterKey]}
            onChange={onMultiSelectCheckboxChange}
            onSearch={onSearch}
          />
        );
      case 'MULTISELECT_DROPDOWN':
        return (
          <MultiselectDropdown
            placeholder={placeholder}
            options={getAvailablePayerOptionsFromState(rootState)}
            value={selectedFilters[filterKey]}
            onChange={onMultiselectDropdownChange}
            onSearch={onSearch}
            filterKey={filterKey}
          />
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <Card className="in-patient-filter-dropdown">
        <Row gutter={GUTTOR_SPACE_SMALL}>
          <Col md={24}>
            {getInputField()}
          </Col>
        </Row>
      </Card>
    </React.Fragment>);
};

export default FilterDropdownComponent;
